import React, { useState, useEffect } from "react";
import axios from "axios";
import { Activity, ArrowLeft, BarChart3, Briefcase, Calendar, ChevronLeft, ChevronRight, ClipboardList, Clock, Download, Edit3, Eye, FileText, FileWarning, Filter, MapPin, Plus, RefreshCw, Save, Truck, User, Users } from "lucide-react";
import { Badge, Button, Card, Input, Modal, Select, Spinner } from "../shared/ui";
import { DateTimePicker } from "../components/DateTimePicker";
import MissedSignoffsReportTab from "../components/compliance/MissedSignoffsReportTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format hours as e.g. "7h 30m"
const formatHours = (hours) => {
  if (!hours || hours === 0) return "0h";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
};

// Per-machine usage analytics tab
const MachineUsageTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/analytics/machine-usage-summary`);
        setData(res.data);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex justify-center py-12" data-testid="machine-usage-tab"><Spinner /></div>;
  if (!data || !data.machines?.length) {
    return <Card className="text-center py-12" data-testid="machine-usage-tab"><p className="text-slate-500">No machine usage records yet</p></Card>;
  }

  const s = data.summary;
  const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

  return (
    <div className="space-y-4" data-testid="machine-usage-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="text-center p-3">
          <p className="text-2xl font-bold">{s.total_machines_used}</p>
          <p className="text-[10px] uppercase text-slate-500 font-bold">Machines Used</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-blue-600">{s.total_deployments}</p>
          <p className="text-[10px] uppercase text-slate-500 font-bold">Deployments</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-green-600">{s.active_deployments}</p>
          <p className="text-[10px] uppercase text-slate-500 font-bold">Active Now</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-orange-600">{fmt(s.total_hours)}h</p>
          <p className="text-[10px] uppercase text-slate-500 font-bold">Machine Hours</p>
        </Card>
        <Card className="text-center p-3">
          <p className="text-2xl font-bold text-purple-600">{fmt(s.total_km)} km</p>
          <p className="text-[10px] uppercase text-slate-500 font-bold">KM Travelled</p>
        </Card>
      </div>

      {/* Machine list with job breakdown */}
      <div className="space-y-3">
        {data.machines.map(m => {
          const isOpen = !!expanded[m.machine_id];
          return (
            <Card key={m.machine_id} className="border-2 border-slate-200" data-testid={`mu-row-${m.machine_id}`}>
              <button type="button" className="w-full text-left" onClick={() => setExpanded(e => ({ ...e, [m.machine_id]: !e[m.machine_id] }))}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Truck className="w-4 h-4 text-slate-500" />
                      <h3 className="font-bold truncate">{m.machine_name}</h3>
                      {m.registration && <span className="text-xs text-slate-500">· {m.registration}</span>}
                      <Badge variant={m.current_status === 'in_use' ? 'success' : m.current_status === 'hired_out' ? 'info' : m.current_status === 'maintenance' ? 'warning' : 'secondary'}>
                        {(m.current_status || 'unknown').replace('_',' ').toUpperCase()}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-600">
                      <span>Deployments: <strong>{m.total_deployments}</strong></span>
                      <span>Hours: <strong className="text-orange-600">{fmt(m.total_hours)}h</strong></span>
                      <span>KM: <strong className="text-purple-600">{fmt(m.total_km)}</strong></span>
                      <span>Days: <strong>{m.total_days}</strong></span>
                      {m.current_km != null && <span className="text-slate-400">Current: {m.current_km} km</span>}
                      {m.current_hours != null && <span className="text-slate-400">/ {m.current_hours}h</span>}
                    </div>
                  </div>
                  <span className="text-xs text-slate-400">{isOpen ? 'Hide' : 'Show'} {m.deployments.length} deployment{m.deployments.length===1?'':'s'}</span>
                </div>
              </button>

              {isOpen && (
                <div className="mt-3 pt-3 border-t border-dashed border-slate-200 overflow-x-auto" data-testid={`mu-deployments-${m.machine_id}`}>
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2 font-bold">Job</th>
                        <th className="text-left p-2 font-bold">Client</th>
                        <th className="text-left p-2 font-bold">Location</th>
                        <th className="text-left p-2 font-bold">Operator</th>
                        <th className="text-left p-2 font-bold">Dates</th>
                        <th className="text-right p-2 font-bold">Hours</th>
                        <th className="text-right p-2 font-bold">KM</th>
                        <th className="text-left p-2 font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.deployments.map(d => (
                        <tr key={d.usage_id} className="border-b border-slate-100">
                          <td className="p-2">{d.job_title || '-'}</td>
                          <td className="p-2">{d.client_name || '-'}</td>
                          <td className="p-2 text-slate-600">{d.location || '-'}</td>
                          <td className="p-2">{d.operator_name || <span className="text-red-500 italic">none</span>}</td>
                          <td className="p-2 text-slate-600">{d.start_date}{d.end_date ? ` → ${d.end_date}` : ''}</td>
                          <td className="p-2 text-right text-orange-600 font-bold">{fmt(d.hours)}</td>
                          <td className="p-2 text-right text-purple-600">{d.km != null ? fmt(d.km) : '-'}</td>
                          <td className="p-2"><Badge variant={d.status === 'active' ? 'success' : 'secondary'}>{d.status}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// Compact machine usage summary for the Analytics Summary tab (top 5 by hours)
const MachineSummaryCards = () => {
  const [machines, setMachines] = useState(null);
  useEffect(() => {
    axios.get(`${API}/analytics/machine-usage-summary`).then(r => setMachines(r.data)).catch(() => setMachines({ machines: [] }));
  }, []);
  if (!machines) return <Spinner size="sm" />;
  const top5 = (machines.machines || []).slice(0, 5);
  if (top5.length === 0) return <p className="text-slate-500 italic text-sm">No machine usage logged yet</p>;
  const fmt = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
  return (
    <div className="space-y-2">
      {top5.map(m => (
        <div key={m.machine_id} className="flex items-center justify-between gap-2 p-2.5 bg-slate-50 rounded-lg" data-testid={`summary-machine-${m.machine_id}`}>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{m.machine_name}</p>
            <p className="text-[11px] text-slate-500">{m.total_deployments} deployment{m.total_deployments !== 1 ? 's' : ''}</p>
          </div>
          <div className="text-right flex items-center gap-4">
            <div>
              <p className="font-bold text-orange-600 text-sm">{fmt(m.total_hours)}h</p>
              <p className="text-[10px] text-slate-500 uppercase">Hours</p>
            </div>
            <div>
              <p className="font-bold text-purple-600 text-sm">{fmt(m.total_km)}</p>
              <p className="text-[10px] text-slate-500 uppercase">KM</p>
            </div>
          </div>
        </div>
      ))}
      {machines.machines.length > 5 && (
        <p className="text-[11px] text-slate-400 text-center pt-1">+ {machines.machines.length - 5} more machines in "Machine Usage" tab</p>
      )}
    </div>
  );
};

// Per-employee weekly view with prev/next week picker
const EmployeeWeeklyView = () => {
  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(date.setDate(diff));
  };
  const fmtISO = (d) => d.toISOString().split('T')[0];
  const fmtShort = (d) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  useEffect(() => {
    const fetchWeek = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/analytics/employee-hours`, {
          params: {
            start_date: fmtISO(weekStart),
            end_date: fmtISO(weekEnd),
            group_by: 'employee'
          }
        });
        setData(res.data);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetchWeek();
  }, [weekStart]);

  const jumpWeek = (delta) => {
    const newStart = new Date(weekStart);
    newStart.setDate(weekStart.getDate() + (delta * 7));
    setWeekStart(newStart);
  };
  const goThisWeek = () => setWeekStart(getMonday(new Date()));

  const isCurrentWeek = fmtISO(weekStart) === fmtISO(getMonday(new Date()));

  return (
    <Card data-testid="employee-weekly-view">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="text-lg font-bold uppercase">Employee Hours — Weekly View</h3>
          <p className="text-xs text-slate-500 mt-1">Flick between weeks using the arrows. Only this week's hours for each employee.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => jumpWeek(-1)} data-testid="week-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="px-3 py-1 bg-slate-100 rounded text-center">
            <p className="text-xs text-slate-500 uppercase font-bold">Week of</p>
            <p className="text-sm font-bold" data-testid="week-label">{fmtShort(weekStart)} — {fmtShort(weekEnd)}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => jumpWeek(1)} disabled={isCurrentWeek} data-testid="week-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isCurrentWeek && (
            <Button size="sm" variant="ghost" onClick={goThisWeek} data-testid="week-this">Today</Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : !data || data.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Clock className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p>No hours logged in this week</p>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Card className="text-center p-3 bg-orange-50">
              <p className="text-2xl font-bold text-orange-600">{data.length}</p>
              <p className="text-[10px] uppercase text-slate-500 font-bold">Employees Worked</p>
            </Card>
            <Card className="text-center p-3 bg-green-50">
              <p className="text-2xl font-bold text-green-600">{formatHours(data.reduce((s, r) => s + (r.total_hours || 0), 0))}</p>
              <p className="text-[10px] uppercase text-slate-500 font-bold">Total Hours This Week</p>
            </Card>
            <Card className="text-center p-3 bg-blue-50">
              <p className="text-2xl font-bold text-blue-600">{data.reduce((s, r) => s + (r.clock_entries || 0), 0)}</p>
              <p className="text-[10px] uppercase text-slate-500 font-bold">Clock Entries</p>
            </Card>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold uppercase text-sm text-slate-600">Employee</th>
                  <th className="text-right py-3 px-4 font-semibold uppercase text-sm text-slate-600">Hours This Week</th>
                  <th className="text-right py-3 px-4 font-semibold uppercase text-sm text-slate-600">Entries</th>
                  <th className="text-right py-3 px-4 font-semibold uppercase text-sm text-slate-600">Avg/Day</th>
                </tr>
              </thead>
              <tbody>
                {data.sort((a, b) => (b.total_hours || 0) - (a.total_hours || 0)).map((row, idx) => {
                  const days = new Set(); // estimate worked-days based on entries? We'll just do hours/7
                  return (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`emp-week-row-${row.employee_id || idx}`}>
                      <td className="py-3 px-4 font-medium">{row.employee_name}</td>
                      <td className="py-3 px-4 text-right font-bold text-orange-600">{formatHours(row.total_hours)}</td>
                      <td className="py-3 px-4 text-right text-slate-500">{row.clock_entries}</td>
                      <td className="py-3 px-4 text-right text-slate-500">{formatHours((row.total_hours || 0) / Math.max(row.clock_entries, 1))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
};

// Analytics Page (Admin only)
const AnalyticsPage = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [jobHours, setJobHours] = useState([]);
  const [employeeHours, setEmployeeHours] = useState([]);
  const [siteTime, setSiteTime] = useState([]);
  const [machineUtilization, setMachineUtilization] = useState([]);
  const [groupBy, setGroupBy] = useState("employee");
  
  // Detail view states
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [machineDetail, setMachineDetail] = useState(null);
  
  // Live attendance states
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState(null);
  const [attendanceJobFilter, setAttendanceJobFilter] = useState("");
  const [jobs, setJobs] = useState([]);
  
  // Monthly report states
  const [reportMonth, setReportMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  // Time entries management states
  const [allTimeEntries, setAllTimeEntries] = useState([]);
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(false);
  const [timeEntriesFilter, setTimeEntriesFilter] = useState({ user_id: "", job_id: "" });
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAddManualEntry, setShowAddManualEntry] = useState(false);
  const [manualEntryData, setManualEntryData] = useState({
    user_id: "", job_id: "", clock_in_time: "", clock_out_time: "", notes: ""
  });
  const [users, setUsers] = useState([]);
  
  // Completed daybook states
  const [completedDaybooks, setCompletedDaybooks] = useState([]);
  const [selectedDaybook, setSelectedDaybook] = useState(null);
  const [daybookPdfData, setDaybookPdfData] = useState(null);

  useEffect(() => {
    fetchData();
    fetchJobs();
  }, [dateRange, groupBy]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = `start_date=${dateRange.start}&end_date=${dateRange.end}`;
      
      const [summaryRes, jobsRes, employeesRes, sitesRes, machinesRes] = await Promise.all([
        axios.get(`${API}/analytics/summary?${params}`),
        axios.get(`${API}/analytics/job-hours?${params}`),
        axios.get(`${API}/analytics/employee-hours?${params}&group_by=${groupBy}`),
        axios.get(`${API}/analytics/site-time?${params}`),
        axios.get(`${API}/analytics/machine-utilization`)
      ]);
      
      setSummary(summaryRes.data);
      setJobHours(jobsRes.data);
      setEmployeeHours(employeesRes.data);
      setSiteTime(sitesRes.data);
      setMachineUtilization(machinesRes.data);
    } catch (e) {
      console.error("Failed to fetch analytics:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs`);
      setJobs(res.data);
    } catch (e) {}
  };

  const fetchJobDetail = async (jobId) => {
    try {
      const res = await axios.get(`${API}/analytics/job-detail/${jobId}?start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setJobDetail(res.data);
      setSelectedJob(jobId);
    } catch (e) {
      console.error("Failed to fetch job detail:", e);
    }
  };

  const fetchMachineDetail = async (machineId) => {
    try {
      const res = await axios.get(`${API}/analytics/machine-detail/${machineId}`);
      setMachineDetail(res.data);
      setSelectedMachine(machineId);
    } catch (e) {
      console.error("Failed to fetch machine detail:", e);
    }
  };

  const fetchAttendance = async () => {
    try {
      let url = `${API}/analytics/live-attendance?date=${attendanceDate}`;
      if (attendanceJobFilter) url += `&job_id=${attendanceJobFilter}`;
      const res = await axios.get(url);
      setAttendanceData(res.data);
    } catch (e) {
      console.error("Failed to fetch attendance:", e);
    }
  };

  const fetchMonthlyReport = async () => {
    try {
      const res = await axios.get(`${API}/analytics/monthly-report?month=${reportMonth}`);
      setMonthlyReport(res.data);
    } catch (e) {
      console.error("Failed to fetch monthly report:", e);
    }
  };

  const fetchTimeEntries = async () => {
    setTimeEntriesLoading(true);
    try {
      let url = `${API}/time/all-entries?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      if (timeEntriesFilter.user_id) url += `&user_id=${timeEntriesFilter.user_id}`;
      if (timeEntriesFilter.job_id) url += `&job_id=${timeEntriesFilter.job_id}`;
      const res = await axios.get(url);
      setAllTimeEntries(res.data);
    } catch (e) {
      console.error("Failed to fetch time entries:", e);
    } finally {
      setTimeEntriesLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (e) {}
  };

  const fetchCompletedDaybooks = async () => {
    try {
      const res = await axios.get(`${API}/daybook/completed?start_date=${dateRange.start}&end_date=${dateRange.end}`);
      setCompletedDaybooks(res.data);
    } catch (e) {
      console.error("Failed to fetch completed daybooks:", e);
    }
  };

  const fetchDaybookPdfData = async (entryId) => {
    try {
      const res = await axios.get(`${API}/daybook/${entryId}/pdf-data`);
      setDaybookPdfData(res.data);
      setSelectedDaybook(entryId);
    } catch (e) {
      console.error("Failed to fetch daybook PDF data:", e);
    }
  };

  const handleUpdateTimeEntry = async () => {
    if (!editingEntry) return;
    try {
      await axios.put(`${API}/time/entries/${editingEntry.id}`, {
        clock_in: editingEntry.clock_in,
        clock_out: editingEntry.clock_out,
        job_id: editingEntry.job_id,
        notes: editingEntry.notes
      });
      setEditingEntry(null);
      fetchTimeEntries();
      alert("Time entry updated!");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update entry");
    }
  };

  const handleAddManualEntry = async () => {
    if (!manualEntryData.user_id || !manualEntryData.job_id || !manualEntryData.clock_in_time) {
      alert("Please fill in User, Job, and Clock In Time");
      return;
    }
    try {
      await axios.post(`${API}/time/admin-clock-in`, null, {
        params: {
          user_id: manualEntryData.user_id,
          job_id: manualEntryData.job_id,
          clock_in_time: manualEntryData.clock_in_time,
          notes: manualEntryData.notes
        }
      });
      setShowAddManualEntry(false);
      setManualEntryData({ user_id: "", job_id: "", clock_in_time: "", clock_out_time: "", notes: "" });
      fetchTimeEntries();
      alert("User clocked in successfully!");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to clock in user");
    }
  };

  const downloadDaybookPDF = async () => {
    if (!daybookPdfData) return;
    try {
      const res = await axios.get(`${API}/daybook/${daybookPdfData.entry.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `daybook_${daybookPdfData.entry.client_name || 'docket'}_${daybookPdfData.entry.work_date || 'unknown'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to generate PDF");
    }
  };

  useEffect(() => {
    if (activeTab === "attendance") {
      fetchAttendance();
    }
  }, [activeTab, attendanceDate, attendanceJobFilter]);

  useEffect(() => {
    if (activeTab === "report") {
      fetchMonthlyReport();
    }
  }, [activeTab, reportMonth]);

  useEffect(() => {
    if (activeTab === "time-entries") {
      fetchTimeEntries();
      fetchUsers();
    }
  }, [activeTab, dateRange, timeEntriesFilter]);

  useEffect(() => {
    if (activeTab === "completed-daybooks") {
      fetchCompletedDaybooks();
    }
  }, [activeTab, dateRange]);

  const formatHours = (hours) => {
    if (!hours) return "0h";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const downloadPDF = async () => {
    if (!monthlyReport) return;
    setGeneratingPDF(true);
    
    // Create PDF content as HTML string
    const pdfContent = `
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          h1 { color: #ea580c; border-bottom: 3px solid #ea580c; padding-bottom: 10px; }
          h2 { color: #475569; margin-top: 30px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { border: 1px solid #e2e8f0; padding: 10px; text-align: left; }
          th { background: #f8fafc; font-weight: bold; }
          .summary-box { background: #fff7ed; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; }
          .summary-item { text-align: center; }
          .summary-value { font-size: 24px; font-weight: bold; color: #ea580c; }
          .summary-label { font-size: 12px; color: #64748b; text-transform: uppercase; }
          .footer { margin-top: 40px; text-align: center; color: #94a3b8; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>Monthly Budget Report - ${reportMonth}</h1>
        <p>Generated: ${new Date(monthlyReport.generated_at).toLocaleString()}</p>
        
        <div class="summary-box">
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-value">${formatHours(monthlyReport.summary.total_work_hours)}</div>
              <div class="summary-label">Work Hours</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${formatHours(monthlyReport.summary.total_travel_hours)}</div>
              <div class="summary-label">Travel Hours</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${monthlyReport.summary.unique_employees}</div>
              <div class="summary-label">Employees</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${monthlyReport.summary.unique_jobs}</div>
              <div class="summary-label">Jobs</div>
            </div>
          </div>
        </div>
        
        <h2>Employee Hours</h2>
        <table>
          <tr><th>Employee</th><th>Role</th><th>Work Hours</th><th>Travel Hours</th><th>Total</th></tr>
          ${monthlyReport.employees.map(e => `
            <tr>
              <td>${e.name}</td>
              <td>${e.role}</td>
              <td>${formatHours(e.work_hours)}</td>
              <td>${formatHours(e.travel_hours)}</td>
              <td><strong>${formatHours(e.work_hours + e.travel_hours)}</strong></td>
            </tr>
          `).join('')}
        </table>
        
        <h2>Job Hours</h2>
        <table>
          <tr><th>Job</th><th>Client</th><th>Location</th><th>Hours</th><th>Workers</th></tr>
          ${monthlyReport.jobs.map(j => `
            <tr>
              <td>${j.title}</td>
              <td>${j.client}</td>
              <td>${j.location}</td>
              <td><strong>${formatHours(j.total_hours)}</strong></td>
              <td>${j.worker_count}</td>
            </tr>
          `).join('')}
        </table>
        
        <h2>Machine Usage</h2>
        <table>
          <tr><th>Machine</th><th>Type</th><th>Hours</th><th>Jobs Used</th></tr>
          ${monthlyReport.machines.map(m => `
            <tr>
              <td>${m.name}</td>
              <td>${m.type}</td>
              <td><strong>${formatHours(m.total_hours)}</strong></td>
              <td>${m.jobs_used}</td>
            </tr>
          `).join('')}
        </table>
        
        ${monthlyReport.summary.total_laa_days > 0 || monthlyReport.summary.total_reimbursements > 0 ? `
          <h2>Allowances & Reimbursements</h2>
          <p><strong>LAA Days:</strong> ${monthlyReport.summary.total_laa_days}</p>
          <p><strong>Total Reimbursements:</strong> $${monthlyReport.summary.total_reimbursements.toFixed(2)}</p>
          ${monthlyReport.reimbursements.length > 0 ? `
            <table>
              <tr><th>Employee</th><th>Category</th><th>Amount</th><th>Description</th></tr>
              ${monthlyReport.reimbursements.map(r => `
                <tr>
                  <td>${r.user}</td>
                  <td>${r.category}</td>
                  <td>$${r.amount?.toFixed(2)}</td>
                  <td>${r.description || ''}</td>
                </tr>
              `).join('')}
            </table>
          ` : ''}
        ` : ''}
        
        <div class="footer">
          <p>ShadowOps - Construction Management System</p>
          <p>Report generated automatically for budgeting purposes</p>
        </div>
      </body>
      </html>
    `;
    
    // Open print dialog with the PDF content
    const printWindow = window.open('', '_blank');
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    printWindow.print();
    
    setGeneratingPDF(false);
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Analytics</h1>
          <p className="text-slate-500 mt-1">Track time, machines, and productivity</p>
        </div>
        
        {/* Date Range Filter */}
        <div className="flex items-center gap-3 bg-white p-3 rounded-lg border-2 border-slate-200">
          <Calendar className="w-5 h-5 text-slate-400" />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
            className="border-none text-sm font-medium"
          />
          <span className="text-slate-400">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
            className="border-none text-sm font-medium"
          />
        </div>
      </div>

      {/* Summary Cards — removed aggregate Total Hours (becomes meaningless with thousands of hours); replaced with This-Week hours + active machine deployments */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="text-center">
            <p className="text-3xl font-bold text-orange-600">{formatHours(summary.hours_this_week || 0)}</p>
            <p className="text-sm text-slate-500 uppercase font-semibold">Hours This Week</p>
            <p className="text-[10px] text-slate-400 mt-1">Mon–Sun</p>
          </Card>
          <Card className="text-center">
            <p className="text-3xl font-bold text-blue-600">{summary.unique_workers}</p>
            <p className="text-sm text-slate-500 uppercase font-semibold">Active Workers</p>
          </Card>
          <Card className="text-center">
            <p className="text-3xl font-bold text-green-600">{summary.unique_jobs}</p>
            <p className="text-sm text-slate-500 uppercase font-semibold">Jobs Worked</p>
          </Card>
          <Card className="text-center">
            <p className="text-3xl font-bold text-purple-600">{summary.machines_in_use || 0}</p>
            <p className="text-sm text-slate-500 uppercase font-semibold">Machines In Use</p>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: "summary", label: "Summary", icon: BarChart3 },
          { id: "jobs", label: "Jobs", icon: Briefcase },
          { id: "machines", label: "Machines", icon: Truck },
          { id: "machine-usage", label: "Machine Usage", icon: Activity },
          { id: "employees", label: "Employees", icon: Users },
          { id: "attendance", label: "Live Attendance", icon: MapPin },
          { id: "time-entries", label: "Time Entries", icon: Clock },
          { id: "completed-daybooks", label: "Completed Daybooks", icon: ClipboardList },
          { id: "missed-signoffs", label: "Missed Sign-offs", icon: FileWarning },
          { id: "report", label: "Monthly Report", icon: FileText }
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`analytics-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md font-semibold text-sm uppercase whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'bg-orange-500 text-slate-900' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Summary Tab */}
      {activeTab === "summary" && summary && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Workers (This Period's selected date range, not all-time) */}
          {/* Top Machines — hours + km (most useful info) */}
          <Card data-testid="summary-top-machines">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold uppercase">Top Machines — Hours & KM</h3>
              <Button size="sm" variant="ghost" onClick={() => setActiveTab('machine-usage')}>
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <MachineSummaryCards />
          </Card>

          {/* Machine Status */}
          <Card>
            <h3 className="text-lg font-bold uppercase mb-4">Machine Status</h3>
            <div className="space-y-3">
              {Object.entries(summary.machine_status || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium capitalize">{status.replace('_', ' ')}</span>
                  <Badge variant={
                    status === 'available' ? 'success' :
                    status === 'in_use' ? 'info' :
                    status === 'maintenance' ? 'warning' : 'error'
                  }>
                    {count} machine{count !== 1 ? 's' : ''}
                  </Badge>
                </div>
              ))}
              {(!summary.machine_status || Object.keys(summary.machine_status).length === 0) && (
                <p className="text-slate-500 text-center py-4">No machines tracked</p>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Jobs Tab - Clickable for detail */}
      {activeTab === "jobs" && !selectedJob && (
        <Card>
          <h3 className="text-lg font-bold uppercase mb-4">Hours by Job - Click for Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold uppercase text-sm text-slate-600">Job</th>
                  <th className="text-left py-3 px-4 font-semibold uppercase text-sm text-slate-600">Client</th>
                  <th className="text-right py-3 px-4 font-semibold uppercase text-sm text-slate-600">Hours</th>
                  <th className="text-right py-3 px-4 font-semibold uppercase text-sm text-slate-600">Workers</th>
                  <th className="text-center py-3 px-4 font-semibold uppercase text-sm text-slate-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {jobHours.map((job, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-orange-50 cursor-pointer" onClick={() => fetchJobDetail(job.job_id)}>
                    <td className="py-3 px-4">
                      <span className="font-medium">{job.job_title}</span>
                      <Badge variant={job.status === 'completed' ? 'success' : job.status === 'in_progress' ? 'info' : 'default'} className="ml-2">
                        {job.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{job.client_name}</td>
                    <td className="py-3 px-4 text-right font-bold text-orange-600">{formatHours(job.total_hours)}</td>
                    <td className="py-3 px-4 text-right text-slate-500">{job.unique_workers}</td>
                    <td className="py-3 px-4 text-center">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); fetchJobDetail(job.job_id); }}>
                        <Eye className="w-4 h-4 mr-1" /> View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {jobHours.length === 0 && (
              <p className="text-slate-500 text-center py-8">No job hours for this period</p>
            )}
          </div>
        </Card>
      )}

      {/* Job Detail View */}
      {activeTab === "jobs" && selectedJob && jobDetail && (
        <div className="space-y-6">
          <div className="flex items-center gap-4 flex-wrap">
            <Button variant="outline" onClick={() => { setSelectedJob(null); setJobDetail(null); }}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Jobs
            </Button>
            <h2 className="text-2xl font-bold flex-1 min-w-0">{jobDetail.job?.title}</h2>
            <Button
              variant="default"
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/jobs/${jobDetail.job.id}/report/pdf`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  const a = document.createElement('a');
                  a.href = url;
                  const safeTitle = (jobDetail.job.title || 'job').replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
                  a.download = `job_report_${safeTitle}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  alert(e.response?.data?.detail || 'Failed to generate job report PDF');
                }
              }}
              data-testid="download-job-report-pdf"
            >
              <FileText className="w-4 h-4 mr-2" /> Download Job Report PDF
            </Button>
          </div>
          
          {/* Job Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="text-center bg-blue-50">
              <p className="text-2xl font-bold text-blue-600">{jobDetail.summary.unique_workers}</p>
              <p className="text-xs text-slate-500 uppercase">Workers</p>
            </Card>
            <Card className="text-center bg-green-50">
              <p className="text-2xl font-bold text-green-600">{jobDetail.summary.days_worked}</p>
              <p className="text-xs text-slate-500 uppercase">Days Worked</p>
            </Card>
            <Card className="text-center bg-orange-50" data-testid="job-detail-machine-hours">
              <p className="text-2xl font-bold text-orange-600">{formatHours(jobDetail.summary.total_machine_hours || 0)}</p>
              <p className="text-xs text-slate-500 uppercase">Machine Hours</p>
            </Card>
            <Card className="text-center bg-purple-50">
              <p className="text-2xl font-bold text-purple-600">{jobDetail.documents?.length || 0}</p>
              <p className="text-xs text-slate-500 uppercase">Documents</p>
            </Card>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Top Hours per Day */}
            <Card data-testid="job-detail-top-hours">
              <h3 className="text-lg font-bold uppercase mb-4">Top Hours Per Day</h3>
              <div className="max-h-80 overflow-y-auto space-y-2">
                {jobDetail.daily_breakdown?.map((day, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium">{new Date(day.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                      <span className="text-xs text-slate-500">{day.worker_count} worker{day.worker_count !== 1 ? 's' : ''}</span>
                    </div>
                    {day.top_worker ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold text-slate-700">{day.top_worker.name}</p>
                          {day.top_workers && day.top_workers.length > 1 && (
                            <p className="text-xs text-slate-400">
                              Next: {day.top_workers.slice(1).map(w => `${w.name} (${formatHours(w.hours)})`).join(', ')}
                            </p>
                          )}
                        </div>
                        <span className="font-bold text-orange-600">{formatHours(day.top_worker.hours)}</span>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">No worker data</p>
                    )}
                  </div>
                ))}
                {(!jobDetail.daily_breakdown || jobDetail.daily_breakdown.length === 0) && (
                  <p className="text-sm text-slate-400 italic text-center py-6">No time entries recorded for this period</p>
                )}
              </div>
            </Card>
            
            {/* Workers on Job */}
            <Card>
              <h3 className="text-lg font-bold uppercase mb-4">Workers</h3>
              <div className="max-h-80 overflow-y-auto space-y-2">
                {jobDetail.worker_breakdown?.map((worker, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{worker.name}</p>
                      <p className="text-xs text-slate-500 capitalize">{worker.role} - {worker.entries} entries</p>
                    </div>
                    <span className="font-bold text-orange-600">{formatHours(worker.hours)}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Machine Usage Breakdown */}
          {jobDetail.machine_breakdown?.length > 0 && (
            <Card data-testid="job-detail-machine-breakdown">
              <h3 className="text-lg font-bold uppercase mb-4">Machine Hours on This Job</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {jobDetail.machine_breakdown.map((m) => (
                  <div key={m.machine_id} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium">{m.machine_name}</p>
                      <p className="text-xs text-slate-500">{m.days} day{m.days !== 1 ? 's' : ''}</p>
                    </div>
                    <span className="font-bold text-orange-600">{formatHours(m.hours)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
          
          {/* Job Documents */}
          {jobDetail.documents?.length > 0 && (
            <Card>
              <h3 className="text-lg font-bold uppercase mb-4">Job Documents</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {jobDetail.documents.map((doc, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-blue-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{doc.name || doc.upload_type}</p>
                        <p className="text-xs text-slate-500">{doc.upload_type} - {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Machines Tab - Clickable for detail */}
      {activeTab === "machines" && !selectedMachine && (
        <div className="space-y-4">
          {machineUtilization.map(machine => (
            <Card key={machine.machine_id} className="hover:border-orange-300 cursor-pointer transition-colors" onClick={() => fetchMachineDetail(machine.machine_id)}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Truck className="w-6 h-6 text-slate-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{machine.machine_name}</h3>
                    <p className="text-sm text-slate-500">{machine.machine_type} - <Badge variant={
                      machine.current_status === 'available' ? 'success' :
                      machine.current_status === 'in_use' ? 'info' :
                      machine.current_status === 'maintenance' ? 'warning' : 'error'
                    }>{machine.current_status?.replace('_', ' ')}</Badge></p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-orange-600">{machine.utilization_rate}%</p>
                    <p className="text-xs text-slate-500 uppercase">Utilization</p>
                  </div>
                  <Button size="sm" variant="outline">
                    <Eye className="w-4 h-4 mr-1" /> Details
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {machineUtilization.length === 0 && (
            <Card className="text-center py-12">
              <Truck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No machine utilization data yet</p>
            </Card>
          )}
        </div>
      )}

      {/* Machine Detail View */}
      {activeTab === "machines" && selectedMachine && machineDetail && (
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => { setSelectedMachine(null); setMachineDetail(null); }}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Machines
            </Button>
            <h2 className="text-2xl font-bold">{machineDetail.machine?.name}</h2>
            <Badge variant={
              machineDetail.machine?.status === 'available' ? 'success' :
              machineDetail.machine?.status === 'in_use' ? 'info' :
              machineDetail.machine?.status === 'maintenance' ? 'warning' : 'error'
            }>{machineDetail.machine?.status}</Badge>
          </div>
          
          {/* Machine Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="text-center bg-orange-50">
              <p className="text-2xl font-bold text-orange-600">{formatHours(machineDetail.summary.total_usage_hours)}</p>
              <p className="text-xs text-slate-500 uppercase">Total Hours</p>
            </Card>
            <Card className="text-center bg-blue-50">
              <p className="text-2xl font-bold text-blue-600">{machineDetail.summary.total_usage_entries}</p>
              <p className="text-xs text-slate-500 uppercase">Usage Entries</p>
            </Card>
            <Card className="text-center bg-green-50">
              <p className="text-2xl font-bold text-green-600">{machineDetail.summary.total_hire_outs}</p>
              <p className="text-xs text-slate-500 uppercase">Hire Outs</p>
            </Card>
            <Card className="text-center bg-purple-50">
              <p className="text-2xl font-bold text-purple-600">{machineDetail.summary.documents_count}</p>
              <p className="text-xs text-slate-500 uppercase">Documents</p>
            </Card>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Usage Logs by Date */}
            <Card>
              <h3 className="text-lg font-bold uppercase mb-4">Usage Logs</h3>
              <div className="max-h-80 overflow-y-auto space-y-2">
                {machineDetail.usage_logs?.map((log, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{log.job_title}</p>
                        <p className="text-xs text-slate-500">{log.start_date} {log.end_date && log.end_date !== log.start_date ? `to ${log.end_date}` : ''}</p>
                      </div>
                      <span className="font-bold text-orange-600">{formatHours(log.total_hours)}</span>
                    </div>
                    {log.notes && <p className="text-sm text-slate-600 mt-2">{log.notes}</p>}
                  </div>
                ))}
                {(!machineDetail.usage_logs || machineDetail.usage_logs.length === 0) && (
                  <p className="text-slate-500 text-center py-4">No usage logs</p>
                )}
              </div>
            </Card>
            
            {/* Machine Documents (Services, Receipts) */}
            <Card>
              <h3 className="text-lg font-bold uppercase mb-4">Documents & Services</h3>
              <div className="max-h-80 overflow-y-auto space-y-2">
                {machineDetail.documents?.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{doc.name}</p>
                      <p className="text-xs text-slate-500">{doc.document_type} - {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
                {(!machineDetail.documents || machineDetail.documents.length === 0) && (
                  <p className="text-slate-500 text-center py-4">No documents</p>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Machine Usage Tab */}
      {activeTab === "machine-usage" && (
        <MachineUsageTab />
      )}

      {/* Employee Hours Tab (weekly with prev/next week picker) */}
      {activeTab === "employees" && (
        <EmployeeWeeklyView />
      )}

      {/* Live Attendance Tab */}
      {activeTab === "attendance" && (
        <div className="space-y-6">
          {/* Filters */}
          <Card>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-400" />
                <input
                  type="date"
                  value={attendanceDate}
                  onChange={(e) => setAttendanceDate(e.target.value)}
                  className="border-2 border-slate-200 rounded-md px-3 py-2 font-medium"
                />
              </div>
              <Select
                value={attendanceJobFilter}
                onChange={(e) => setAttendanceJobFilter(e.target.value)}
                options={[
                  { value: "", label: "All Sites" },
                  ...jobs.map(j => ({ value: j.id, label: j.title }))
                ]}
              />
              <Button variant="outline" onClick={fetchAttendance}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>
          </Card>
          
          {attendanceData && (
            <>
              {/* Attendance Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="text-center bg-green-50">
                  <p className="text-2xl font-bold text-green-600">{attendanceData.summary.active_now}</p>
                  <p className="text-xs text-slate-500 uppercase">Currently On Site</p>
                </Card>
                <Card className="text-center bg-blue-50">
                  <p className="text-2xl font-bold text-blue-600">{attendanceData.summary.completed}</p>
                  <p className="text-xs text-slate-500 uppercase">Completed</p>
                </Card>
                <Card className="text-center bg-orange-50">
                  <p className="text-2xl font-bold text-orange-600">{attendanceData.summary.total_entries}</p>
                  <p className="text-xs text-slate-500 uppercase">Total Entries</p>
                </Card>
                <Card className="text-center bg-purple-50">
                  <p className="text-2xl font-bold text-purple-600">{attendanceData.summary.unique_workers}</p>
                  <p className="text-xs text-slate-500 uppercase">Workers</p>
                </Card>
                <Card className="text-center bg-slate-50">
                  <p className="text-2xl font-bold text-slate-600">{attendanceData.summary.unique_sites}</p>
                  <p className="text-xs text-slate-500 uppercase">Sites</p>
                </Card>
              </div>
              
              {/* Attendance by Location */}
              {Object.entries(attendanceData.by_location || {}).map(([location, records]) => (
                <Card key={location}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-orange-500" />
                      {location}
                    </h3>
                    <Badge variant="info">{records.length} worker{records.length !== 1 ? 's' : ''}</Badge>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="text-left py-2 px-3 font-semibold text-sm text-slate-600">Worker</th>
                          <th className="text-left py-2 px-3 font-semibold text-sm text-slate-600">Role</th>
                          <th className="text-left py-2 px-3 font-semibold text-sm text-slate-600">Clock In</th>
                          <th className="text-left py-2 px-3 font-semibold text-sm text-slate-600">Clock Out</th>
                          <th className="text-right py-2 px-3 font-semibold text-sm text-slate-600">Hours</th>
                          <th className="text-center py-2 px-3 font-semibold text-sm text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((record, idx) => (
                          <tr key={idx} className="border-b border-slate-100">
                            <td className="py-2 px-3 font-medium">{record.user_name}</td>
                            <td className="py-2 px-3 text-slate-600 capitalize text-sm">{record.role}</td>
                            <td className="py-2 px-3 text-sm">{record.clock_in ? new Date(record.clock_in).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                            <td className="py-2 px-3 text-sm">{record.clock_out ? new Date(record.clock_out).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                            <td className="py-2 px-3 text-right font-bold text-orange-600">{record.hours ? formatHours(record.hours) : '-'}</td>
                            <td className="py-2 px-3 text-center">
                              <Badge variant={record.status === 'active' ? 'success' : 'default'}>
                                {record.status === 'active' ? 'On Site' : 'Done'}
                              </Badge>
                              {record.type === 'travel' && <Badge variant="info" className="ml-1">Travel</Badge>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      {/* Missed Sign-offs Tab */}
      {activeTab === "missed-signoffs" && (
        <MissedSignoffsReportTab />
      )}

      {/* Monthly Report Tab */}
      {activeTab === "report" && (
        <div className="space-y-6">
          {/* Month Selector */}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <label className="font-semibold">Select Month:</label>
                <input
                  type="month"
                  value={reportMonth}
                  onChange={(e) => setReportMonth(e.target.value)}
                  className="border-2 border-slate-200 rounded-md px-3 py-2 font-medium"
                />
                <Button variant="outline" onClick={fetchMonthlyReport}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Generate Report
                </Button>
              </div>
              {monthlyReport && (
                <Button onClick={downloadPDF} disabled={generatingPDF}>
                  <Download className="w-4 h-4 mr-2" />
                  {generatingPDF ? 'Generating...' : 'Download PDF'}
                </Button>
              )}
            </div>
          </Card>
          
          {monthlyReport && (
            <>
              {/* Report Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="text-center bg-orange-50">
                  <p className="text-2xl font-bold text-orange-600">{formatHours(monthlyReport.summary.total_work_hours)}</p>
                  <p className="text-xs text-slate-500 uppercase">Work Hours</p>
                </Card>
                <Card className="text-center bg-blue-50">
                  <p className="text-2xl font-bold text-blue-600">{formatHours(monthlyReport.summary.total_travel_hours)}</p>
                  <p className="text-xs text-slate-500 uppercase">Travel Hours</p>
                </Card>
                <Card className="text-center bg-green-50">
                  <p className="text-2xl font-bold text-green-600">{monthlyReport.summary.unique_employees}</p>
                  <p className="text-xs text-slate-500 uppercase">Employees</p>
                </Card>
                <Card className="text-center bg-purple-50">
                  <p className="text-2xl font-bold text-purple-600">${monthlyReport.summary.total_reimbursements.toFixed(2)}</p>
                  <p className="text-xs text-slate-500 uppercase">Reimbursements</p>
                </Card>
              </div>
              
              {/* Employee Summary Table */}
              <Card>
                <h3 className="text-lg font-bold uppercase mb-4">Employee Hours Summary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Employee</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Role</th>
                        <th className="text-right py-3 px-4 font-semibold text-sm text-slate-600">Work Hours</th>
                        <th className="text-right py-3 px-4 font-semibold text-sm text-slate-600">Travel Hours</th>
                        <th className="text-right py-3 px-4 font-semibold text-sm text-slate-600">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyReport.employees.map((emp, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-3 px-4 font-medium">{emp.name}</td>
                          <td className="py-3 px-4 text-slate-600 capitalize">{emp.role}</td>
                          <td className="py-3 px-4 text-right">{formatHours(emp.work_hours)}</td>
                          <td className="py-3 px-4 text-right">{formatHours(emp.travel_hours)}</td>
                          <td className="py-3 px-4 text-right font-bold text-orange-600">{formatHours(emp.work_hours + emp.travel_hours)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              
              {/* Job Summary Table */}
              <Card>
                <h3 className="text-lg font-bold uppercase mb-4">Job Hours Summary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Job</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Client</th>
                        <th className="text-right py-3 px-4 font-semibold text-sm text-slate-600">Hours</th>
                        <th className="text-right py-3 px-4 font-semibold text-sm text-slate-600">Workers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyReport.jobs.map((job, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-3 px-4 font-medium">{job.title}</td>
                          <td className="py-3 px-4 text-slate-600">{job.client}</td>
                          <td className="py-3 px-4 text-right font-bold text-orange-600">{formatHours(job.total_hours)}</td>
                          <td className="py-3 px-4 text-right">{job.worker_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              
              {/* Machine Summary Table */}
              <Card>
                <h3 className="text-lg font-bold uppercase mb-4">Machine Usage Summary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Machine</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Type</th>
                        <th className="text-right py-3 px-4 font-semibold text-sm text-slate-600">Hours</th>
                        <th className="text-right py-3 px-4 font-semibold text-sm text-slate-600">Jobs Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyReport.machines.map((machine, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-3 px-4 font-medium">{machine.name}</td>
                          <td className="py-3 px-4 text-slate-600">{machine.type}</td>
                          <td className="py-3 px-4 text-right font-bold text-orange-600">{formatHours(machine.total_hours)}</td>
                          <td className="py-3 px-4 text-right">{machine.jobs_used}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Time Entries Tab - Admin can edit/add entries */}
      {activeTab === "time-entries" && (
        <div className="space-y-6">
          {/* Filters and Add Button */}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <Select
                  label="Filter by Employee"
                  value={timeEntriesFilter.user_id}
                  onChange={(e) => setTimeEntriesFilter({...timeEntriesFilter, user_id: e.target.value})}
                  options={[
                    { value: "", label: "All Employees" },
                    ...users.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }))
                  ]}
                />
                <Select
                  label="Filter by Job"
                  value={timeEntriesFilter.job_id}
                  onChange={(e) => setTimeEntriesFilter({...timeEntriesFilter, job_id: e.target.value})}
                  options={[
                    { value: "", label: "All Jobs" },
                    ...jobs.map(j => ({ value: j.id, label: j.title }))
                  ]}
                />
              </div>
              <Button onClick={() => setShowAddManualEntry(true)}>
                <Plus className="w-4 h-4 mr-2" /> Clock In User
              </Button>
            </div>
          </Card>

          {/* Time Entries Table */}
          <Card>
            <h3 className="text-lg font-bold uppercase mb-4">Time Entries ({allTimeEntries.length})</h3>
            {timeEntriesLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Employee</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Job</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Clock In</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Clock Out</th>
                      <th className="text-right py-3 px-4 font-semibold text-sm text-slate-600">Hours</th>
                      <th className="text-center py-3 px-4 font-semibold text-sm text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTimeEntries.map(entry => (
                      <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <p className="font-medium">{entry.user_name}</p>
                          <p className="text-xs text-slate-500 capitalize">{entry.user_role}</p>
                        </td>
                        <td className="py-3 px-4 text-sm">{entry.job_title}</td>
                        <td className="py-3 px-4 text-sm">
                          {entry.clock_in ? new Date(entry.clock_in).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                          {entry.clock_in_modified && <Badge variant="warning" className="ml-1">Edited</Badge>}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {entry.clock_out ? new Date(entry.clock_out).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }) : <Badge variant="success">Active</Badge>}
                          {entry.clock_out_modified && <Badge variant="warning" className="ml-1">Edited</Badge>}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-orange-600">
                          {entry.total_hours ? formatHours(entry.total_hours) : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button size="sm" variant="outline" onClick={() => setEditingEntry({...entry})}>
                            <Edit3 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {allTimeEntries.length === 0 && (
                  <p className="text-slate-500 text-center py-8">No time entries found for this period</p>
                )}
              </div>
            )}
          </Card>

          {/* Edit Entry Modal */}
          {editingEntry && (
            <Modal isOpen={true} onClose={() => setEditingEntry(null)} title="Edit Time Entry" size="lg">
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-bold">{editingEntry.user_name}</p>
                  <p className="text-sm text-slate-500">{editingEntry.job_title}</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <DateTimePicker
                    label="Clock In"
                    value={editingEntry.clock_in || ''}
                    onChange={(iso) => setEditingEntry({...editingEntry, clock_in: iso})}
                    testId="edit-entry-clock-in"
                  />
                  <DateTimePicker
                    label="Clock Out"
                    value={editingEntry.clock_out || ''}
                    onChange={(iso) => setEditingEntry({...editingEntry, clock_out: iso})}
                    minDate={editingEntry.clock_in ? new Date(editingEntry.clock_in) : undefined}
                    testId="edit-entry-clock-out"
                  />
                </div>
                <Select
                  label="Change Job"
                  value={editingEntry.job_id || ''}
                  onChange={(e) => setEditingEntry({...editingEntry, job_id: e.target.value})}
                  options={[
                    { value: "", label: "No Job" },
                    ...jobs.map(j => ({ value: j.id, label: j.title }))
                  ]}
                />
                <Input
                  label="Notes"
                  value={editingEntry.notes || ''}
                  onChange={(e) => setEditingEntry({...editingEntry, notes: e.target.value})}
                  placeholder="Reason for edit..."
                />
                <div className="flex gap-3 pt-4">
                  <Button onClick={handleUpdateTimeEntry}>Save Changes</Button>
                  <Button variant="ghost" onClick={() => setEditingEntry(null)}>Cancel</Button>
                </div>
              </div>
            </Modal>
          )}

          {/* Add Manual Entry Modal */}
          {showAddManualEntry && (
            <Modal isOpen={true} onClose={() => setShowAddManualEntry(false)} title="Clock In User Manually" size="lg">
              <div className="space-y-4">
                <Select
                  label="Select Employee *"
                  value={manualEntryData.user_id}
                  onChange={(e) => setManualEntryData({...manualEntryData, user_id: e.target.value})}
                  options={[
                    { value: "", label: "-- Select Employee --" },
                    ...users.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name} (${u.role})` }))
                  ]}
                />
                <Select
                  label="Select Job *"
                  value={manualEntryData.job_id}
                  onChange={(e) => setManualEntryData({...manualEntryData, job_id: e.target.value})}
                  options={[
                    { value: "", label: "-- Select Job --" },
                    ...jobs.map(j => ({ value: j.id, label: j.title }))
                  ]}
                />
                <DateTimePicker
                  label="Clock In Time *"
                  value={manualEntryData.clock_in_time}
                  onChange={(iso) => setManualEntryData({...manualEntryData, clock_in_time: iso})}
                  maxDate={new Date()}
                  required
                  testId="manual-entry-clock-in-time"
                />
                <Input
                  label="Notes (optional)"
                  value={manualEntryData.notes}
                  onChange={(e) => setManualEntryData({...manualEntryData, notes: e.target.value})}
                  placeholder="Reason for manual entry..."
                />
                <div className="flex gap-3 pt-4">
                  <Button onClick={handleAddManualEntry}>Clock In User</Button>
                  <Button variant="ghost" onClick={() => setShowAddManualEntry(false)}>Cancel</Button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      )}

      {/* Completed Daybooks Tab */}
      {activeTab === "completed-daybooks" && (
        <div className="space-y-6">
          {!selectedDaybook ? (
            <>
              <Card>
                <h3 className="text-lg font-bold uppercase mb-4">Completed Daywork Dockets ({completedDaybooks.length})</h3>
                <p className="text-sm text-slate-500 mb-4">View all signed daybooks for invoicing. Click to see full details and download PDF.</p>
                
                {completedDaybooks.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Date</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Job</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Client</th>
                          <th className="text-left py-3 px-4 font-semibold text-sm text-slate-600">Type</th>
                          <th className="text-center py-3 px-4 font-semibold text-sm text-slate-600">Labour</th>
                          <th className="text-center py-3 px-4 font-semibold text-sm text-slate-600">Equipment</th>
                          <th className="text-center py-3 px-4 font-semibold text-sm text-slate-600">Photos</th>
                          <th className="text-center py-3 px-4 font-semibold text-sm text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completedDaybooks.map(entry => (
                          <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium">{new Date(entry.work_date).toLocaleDateString('en-AU')}</td>
                            <td className="py-3 px-4">{entry.job_title}</td>
                            <td className="py-3 px-4">{entry.client_name}</td>
                            <td className="py-3 px-4 capitalize">{entry.work_type?.replace('_', ' ')}</td>
                            <td className="py-3 px-4 text-center">{entry.labour_entries?.length || 0}</td>
                            <td className="py-3 px-4 text-center">{entry.equipment_entries?.length || 0}</td>
                            <td className="py-3 px-4 text-center">{entry.photos?.length || 0}</td>
                            <td className="py-3 px-4 text-center">
                              <Button size="sm" variant="outline" onClick={() => fetchDaybookPdfData(entry.id)}>
                                <Eye className="w-4 h-4 mr-1" /> View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No completed daybooks found for this period</p>
                )}
              </Card>
            </>
          ) : daybookPdfData && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={() => { setSelectedDaybook(null); setDaybookPdfData(null); }}>
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to List
                </Button>
                <Button onClick={downloadDaybookPDF}>
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>

              {/* Daybook Detail View */}
              <Card className="border-2 border-orange-200">
                <div className="border-b-2 border-orange-200 pb-4 mb-4">
                  <h2 className="text-2xl font-bold text-orange-600">DAYWORK DOCKET</h2>
                  <p className="text-slate-500">#{daybookPdfData.entry.id.slice(0, 8)}</p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Date</p>
                    <p className="font-medium">{new Date(daybookPdfData.entry.work_date).toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Job Site</p>
                    <p className="font-medium">{daybookPdfData.entry.job_title || daybookPdfData.job?.title}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Client/Builder</p>
                    <p className="font-medium">{daybookPdfData.entry.client_name || daybookPdfData.client?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Work Type</p>
                    <p className="font-medium capitalize">{daybookPdfData.entry.work_type?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Location Details</p>
                    <p className="font-medium">{daybookPdfData.entry.location_details || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase text-slate-500">Status</p>
                    <Badge variant="success">{daybookPdfData.entry.status}</Badge>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-xs font-bold uppercase text-slate-500 mb-2">Description of Works</p>
                  <p className="bg-slate-50 p-4 rounded-lg">{daybookPdfData.entry.description}</p>
                </div>

                {daybookPdfData.entry.work_summary && (
                  <div className="mb-6">
                    <p className="text-xs font-bold uppercase text-slate-500 mb-2">Work Summary</p>
                    <p className="bg-purple-50 p-4 rounded-lg border border-purple-200">{daybookPdfData.entry.work_summary}</p>
                  </div>
                )}
              </Card>

              {/* Labour & Equipment */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-green-50">
                  <h3 className="text-lg font-bold uppercase mb-4">Labour ({daybookPdfData.calculated.total_labour_entries} entries, {daybookPdfData.calculated.labour_total_hours}h)</h3>
                  <div className="space-y-2">
                    {daybookPdfData.entry.labour_entries?.map((l, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="font-bold">{l.qty}x {l.role?.replace('_', ' ')}</span>
                          <span className="text-sm">{l.start_time} - {l.end_time}</span>
                        </div>
                        {l.description && <p className="text-sm text-slate-600 mt-1">{l.description}</p>}
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="bg-orange-50">
                  <h3 className="text-lg font-bold uppercase mb-4">Equipment ({daybookPdfData.calculated.total_equipment_entries} entries, {daybookPdfData.calculated.equipment_total_hours}h)</h3>
                  <div className="space-y-2">
                    {daybookPdfData.entry.equipment_entries?.map((e, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="font-bold">{e.qty}x {e.machine_name}</span>
                          <span className="text-sm">{e.start_time} - {e.end_time}</span>
                        </div>
                        {e.description && <p className="text-sm text-slate-600 mt-1">{e.description}</p>}
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Photos */}
              {daybookPdfData.entry.photos?.length > 0 && (
                <Card>
                  <h3 className="text-lg font-bold uppercase mb-4">Photos ({daybookPdfData.entry.photos.length})</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {daybookPdfData.entry.photos.map((photo, idx) => (
                      <img key={idx} src={photo} alt={`Photo ${idx + 1}`} className="w-full h-48 object-cover rounded-lg border-2 border-slate-200" />
                    ))}
                  </div>
                </Card>
              )}

              {/* Signatures */}
              <Card>
                <h3 className="text-lg font-bold uppercase mb-4">Signatures</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-bold mb-2">Client Signature</p>
                    {daybookPdfData.entry.client_signature?.signature_data ? (
                      <>
                        <img src={daybookPdfData.entry.client_signature.signature_data} alt="Client Signature" className="max-w-[200px] border bg-white p-2 rounded" />
                        <p className="text-sm mt-2"><strong>Name:</strong> {daybookPdfData.entry.client_signature.name}</p>
                        <p className="text-xs text-slate-500">Signed: {new Date(daybookPdfData.entry.client_signature.signed_at).toLocaleString()}</p>
                      </>
                    ) : (
                      <p className="text-slate-500">Not signed</p>
                    )}
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-bold mb-2">Representative Signature</p>
                    {daybookPdfData.entry.rep_signature?.signature_data ? (
                      <>
                        <img src={daybookPdfData.entry.rep_signature.signature_data} alt="Rep Signature" className="max-w-[200px] border bg-white p-2 rounded" />
                        <p className="text-xs text-slate-500 mt-2">Signed: {new Date(daybookPdfData.entry.rep_signature.signed_at).toLocaleString()}</p>
                      </>
                    ) : (
                      <p className="text-slate-500">Not signed</p>
                    )}
                  </div>
                </div>
              </Card>

              {/* Terms and Conditions */}
              <Card>
                <h3 className="text-lg font-bold uppercase mb-4">Terms and Conditions</h3>
                <pre className="text-sm bg-slate-50 p-4 rounded-lg whitespace-pre-wrap">{daybookPdfData.terms_and_conditions}</pre>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
